import React, { useContext, useEffect, useState } from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import QRCode from 'qrcode';
import { format } from 'date-fns';
import { IDeliveryBatch } from '../../../interfaces/delivery-company/tasks';
import { deliveryBatchToQRcodeID } from '../../../utils/delivery-tasks';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { encodePermanentPackage } from '../../../utils/encoding';
import { displayLocation } from '../../../utils/locations';
import { Workflow } from '../../../enums/orders';
import { display_phone_number } from '../../../utils/phone_numbers';
import { registerFonts } from '../../../utils/pdf-fonts';
import { IMerchantOrdersInvoice } from '../../../interfaces/common/merchant-invoices';
import { merchantInvoiceTaskToQRcodeID } from '../../../utils/merchant-invoices';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { displayMoney } from '../../../utils/money';
import { ShopLocationType } from '../../../interfaces/delivery-company/pickups';

registerFonts();

const styles = StyleSheet.create({
  page: {
    padding: 2,
    fontSize: 14,
    fontFamily: 'Noto Sans Arabic',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'row', // Horizontal layout
    alignItems: 'flex-start', // Align content to the top
  },
  qrContainer: {
    width: '40%', // QR code takes up 40% of the width
    alignItems: 'center',
    justifyContent: 'center',
  },
  qrCode: {
    width: 100, // Size of the QR code
    height: 100,
  },
  contentContainer: {
    width: '60%', // Remaining space for other data
    paddingLeft: 5, // Add some spacing between QR code and content
    paddingRight: 5,
    marginTop: 10,
    fontWeight: 'bold',
    height: '100%',
  },
  header: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 2,
    color: '#222',
  },
  section: {
    marginBottom: 2,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 1,
    fontSize: 13,
  },
  cell: {
    fontSize: 11,
    color: '#333',
  },
  date: {
    fontSize: 8,
    margin: 4,
  },
  footer: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    fontSize: 11,
    color: '#555',
  },
});

export const generateQRCode = async (text: string) => {
  try {
    return await QRCode.toDataURL(text);
  } catch (err) {
    console.error(err);
    return '';
  }
};

interface InvoiceStickerProps {
  merchantInvoice: IMerchantOrdersInvoice;
  orgId: string;
  paymentLocation: ShopLocationType | null
}
export const InvoiceSticker = ({
  merchantInvoice,
  paymentLocation,
  orgId,
}: InvoiceStickerProps) => {
  const [qrCode, setQRCode] = useState('');

  useEffect(() => {
    generateQRCode(merchantInvoiceTaskToQRcodeID(merchantInvoice)).then(
      setQRCode
    );
  }, [merchantInvoice.id]);

  const { user, config } = useContext(AuthContext);

  return (
    <Document>
      <Page
        size={{ width: 150, height: 275 }}
        style={styles.page}
        orientation="landscape"
        wrap={false}
      >
        {/* QR Code at the top-left corner */}
        <View style={styles.qrContainer}>
          {qrCode && <Image style={styles.qrCode} src={qrCode} />}
          <Text>{merchantInvoiceTaskToQRcodeID(merchantInvoice)}</Text>
        </View>

        {/* Other data to the right of the QR code */}
        <View style={styles.contentContainer}>
          <View style={styles.header}>
            <Text>Invoice #{merchantInvoice.id}</Text>
          </View>

          <View style={styles.date}>
            <Text>
              {format(
                new Date(merchantInvoice.created_at ?? ''),
                'dd-MM-yyyy HH:mm'
              )}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.cell}>
              {merchantInvoice.merchant?.name}{' '}
              {merchantInvoice.merchant?.phone_number}
            </Text>
           
          </View>
          <View style={styles.row}>
             <Text style={styles.cell}>
              {paymentLocation?.area ? `${paymentLocation.area.district}\n${paymentLocation.area.name_en} | ${paymentLocation.area.name_ar}` : ''}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.cell}>
              Net Due: {displayMoney(merchantInvoice.net_due_to_merchant)}
            </Text>
          </View>
          <Text style={styles.cell}>{merchantInvoice.notes ?? ''}</Text>
          <View style={{ position: 'absolute', right: 1, bottom: 1 }}>
            <Text style={{ fontSize: 17 }}>{orgId}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};
